import { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H3 } from "../../../components/Heading";
import { Input } from "../../../components/Input";
import { Paragraph } from "../../../components/Paragraph";
import { TextArea } from "../../../components/TextArea";
import { FormQuestion } from "./FormQuestion";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import deleteIcon from "../../../assets/settings/delete-red.svg";
import editIcon from "../../../assets/settings/edit.svg";
import { Image } from "../../../components/Image";
import { GrayCard } from "../../../components/Card";
import { useDeleteSection } from "../../../data/useDeleteSection";
import { queryClient } from "../../../queryClient";
import { showError } from "../../../utils/error-handling";
import { FormRow } from "../../../components/Form";
import { useFormSections } from "../../../data/useFormSections";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTE } from "../../../routes";
import { useCreateSection } from "../../../data/useCreateSection";
import { notify } from "../../../utils/notify";
import { useUpdateSection } from "../../../data/useUpdateSection";
import { Modal } from "../../../components/Modal";
import { useQuestionsFiledTypeList } from "../../../data/useQuestionsFiledTypeList";
import { RiDeleteBin6Line, RiDraggable } from "react-icons/ri";
import { FaPlus, FaTrash } from "react-icons/fa";
import { MdModeEdit } from "react-icons/md";
import * as icons from "../../../assets/questions";
import { getValidationSchema } from "../../../utils/validationSchema";
import styled from "styled-components";
import { FormConditions } from "./FormConditions";
import { IconButtonDropdown } from "../../../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../../../components/IconButtonDropdown/IconButtonDropdownOption";
import { useDeleteSectionCondition } from "../../../data/useDeleteSectionCondition";
import { useConditionsList } from "../../../data/useConditionList";

const Container = styled.div`
  ${({ theme: { space } }) => `
    padding-bottom: ${space[5]}
  `}
`;

const StyledUl = styled.ul`
  ${({ theme: { fontSizes, fontFamily } }) => `
      list-style-type: none;
      margin-top: auto;
      margin: 0;
      list-style: none;
      font-family: ${fontFamily};
      font-size: ${fontSizes[1]};
      list-style-type: none;
      padding: 0;
    `}
`;

const SettingsNewFormQuestions = ({ initialFormData = null }) => {
  const params = useParams();
  const navigate = useNavigate();
  const maxQuestions = 10;
  const [sections, setSections] = useState(
    initialFormData?.sections || [
      { id: "", title: "", description: "", questions: [], isEditing: true },
    ]
  );
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [sectionToDeleteIndex, setSectionToDeleteIndex] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [visibleSection, setVisibleSection] = useState("all");
  const { mutate: deleteSection } = useDeleteSection();
  const { mutate: createSection } = useCreateSection();
  const { mutate: updateSection } = useUpdateSection();
  const { mutate: deleteSectionCondition } = useDeleteSectionCondition();
  const { data: fetchedSections, isLoading: isSectionsLoading } =
    useFormSections(parseInt(params.id));
  const { data: questionsFieldTypeList } = useQuestionsFiledTypeList();
  const [isConditioning, setIsConditioning] = useState(false);
  const [isAllConditionsDelete, setIsAllConditionsDelete] = useState(false);
  const [sectionToDeleteId, setSectionToDeleteId] = useState(null);
  const { data: conditionsList } = useConditionsList(sectionToDeleteId ?? 0);

  useEffect(() => {
    if (fetchedSections && fetchedSections.length > 0) {
      setSections(fetchedSections);
    }
  }, [fetchedSections]);
  useEffect(() => {
    if (
      !isSectionsLoading &&
      fetchedSections.length === 0 &&
      sections.length === 1 &&
      sections[0].isEditing === true
    ) {
      setEditingSectionIndex(0);
    }
  }, [
    sections,
    fetchedSections,
    isSectionsLoading,
    conditionsList,
    sectionToDeleteId,
  ]);
  const handleToggleEditSection = (index) => {
    const updatedSections = sections.map((section, i) => ({
      ...section,
      isEditing: i === index ? !section.isEditing : section.isEditing,
    }));
    if (
      !updatedSections[index].id &&
      !updatedSections[index].isEditing &&
      updatedSections[index].questions.length === 0
    ) {
      updatedSections.splice(index, 1);
      setEditingSectionIndex(null);
    } else {
      setEditingSectionIndex(updatedSections[index].isEditing ? index : null);
    }
    setSections(updatedSections);
  };
  const handleSaveSection = async (index, props) => {
    setEditingSectionIndex(null);
    const updatedSections = [...sections];
    const sectionToSave = { ...updatedSections[index] };
    delete sectionToSave.isEditing;
    if (
      sectionToSave._form &&
      typeof sectionToSave._form === "object" &&
      sectionToSave._form.id
    ) {
      sectionToSave._form = sectionToSave._form.id;
    }
    const sectionId = sectionToSave.id ? sectionToSave.id : null;
    delete sectionToSave.id;
    if (sectionToSave.questions) {
      sectionToSave.questions = sectionToSave.questions.map((question) => {
        if (question.id) {
          return {
            ...question,
            field_type: question?.field_type?.id || question?.field_type,
          };
        } else {
          return question;
        }
      });
    }

    delete sectionToSave.condition;

    if (sectionId) {
      updateSection(
        { params: sectionToSave, sectionId: sectionId },
        {
          onSuccess: (res) => {
            notify("Form details updated successfully!", {
              type: "success",
              position: "bottom-left",
            });
            updatedSections[index].isEditing = false;
            setEditingSectionIndex(null);
            setSections(updatedSections);
            props.setFieldValue("sections", updatedSections);
            queryClient.invalidateQueries(["formSections"]);
            // navigate(`/settings/forms/${res.data._form.id}/summary`);
          },
          onError: (err) => {
            showError(err, "A problem occurred while creating the section.");
            updatedSections[index].isEditing = true; // Keep edit mode even on error
          },
        }
      );
    } else {
      createSection(sectionToSave, {
        onSuccess: (res) => {
          notify("Form details updated successfully!", {
            type: "success",
            position: "bottom-left",
          });
          updatedSections[index].isEditing = false;
          setEditingSectionIndex(null);
          setSections(updatedSections);
          props.setFieldValue("sections", updatedSections);
          queryClient.invalidateQueries(["formSections"]);
          //navigate(`/settings/forms/${res.data._form.id}/summary`);
        },
        onError: (err) => {
          showError(err, "A problem occurred while creating the section.");
          updatedSections[index].isEditing = true; // Keep edit mode even on error
        },
      });
    }
  };
  const handleSectionChange = (index, field, value, props) => {
    const updatedSections = [...sections];
    updatedSections[index][field] = value;
    setSections(updatedSections);
    props.setFieldValue(`sections[${index}].${field}`, value);
  };
  const handleAddSection = (props) => {
    const newSection = {
      title: "",
      description: "",
      questions: [],
      isEditing: true,
    };
    const newSections = [...sections, newSection];
    setSections(newSections);
    // Set the editing index to the new section's index
    setEditingSectionIndex(newSections.length - 1);
    props.setFieldValue("sections", newSections);
  };
  const handleAddQuestion = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentQuestion(null);
    setModalOpen(true);
  };
  const handleNavigation = () => {
    navigate(`/settings/forms/${sections[0]._form.id}/summary`);
  };
  const handleSaveQuestion = (newQuestion, props) => {
    const updatedSections = [...sections];
    const section = { ...updatedSections[currentSectionIndex] };
    section.questions = [...section.questions];

    if (currentQuestion) {
      // Use tracked index instead of relying on ID
      section.questions[currentQuestionIndex] = {
        ...newQuestion,
        order: section.questions[currentQuestionIndex].order,
      };
    } else {
      section.questions.push({
        ...newQuestion,
        order: section.questions.length,
      });
    }

    section._form = params.id;
    updatedSections[currentSectionIndex] = section;
    setSections(updatedSections);
    props.setFieldValue("sections", updatedSections);

    // Reset modal states
    setCurrentQuestion(null);
    setCurrentQuestionIndex(null);
    setModalOpen(false);
  };

  const handleRemoveSection = (sectionIndex, props) => {
    deleteSection(sections[sectionIndex].id, {
      onSuccess: () => {
        queryClient.invalidateQueries(["formSections"]);
        const updatedSections = sections.filter((_, i) => i !== sectionIndex);
        setSections(updatedSections);
        props.setFieldValue("sections", updatedSections);
      },
      onError: (err) =>
        showError(err, "A problem occurred while deleting the section."),
    });
    setSectionToDeleteIndex(null);
  };
  const onDragEnd = (result, sectionIndex, props) => {
    if (!result.destination) return;
    // Deep copy of sections to ensure proper reactivity
    const updatedSections = JSON.parse(JSON.stringify(sections));
    const section = updatedSections[sectionIndex];

    // Reorder questions
    const [reorderedQuestion] = section.questions.splice(
      result.source.index,
      1
    );
    section.questions.splice(result.destination.index, 0, reorderedQuestion);
    // Update order property to reflect new indices
    section.questions.forEach((question, index) => {
      question.order = index;
    });

    // Persist the updated order in the state
    setSections(updatedSections);

    // Ensure Formik gets the updated value properly
    setTimeout(() => {
      props.setFieldValue("sections", updatedSections);
    }, 0);
  };

  const handleEditQuestion = (id, sectionIndex, questionIndex, props) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentQuestion(sections[sectionIndex].questions[questionIndex]);
    setCurrentQuestionIndex(questionIndex);
    setModalOpen(true);
  };

  const handleDeleteQuestion = (sectionIndex, questionIndex, props) => {
    const updatedSections = [...sections];
    // Remove the question from the specified section
    updatedSections[sectionIndex].questions = updatedSections[
      sectionIndex
    ].questions.filter((_, qIndex) => qIndex !== questionIndex);
    setSections(updatedSections);
    props.setFieldValue("sections", updatedSections);
  };
  const handleAddConditions = (sectionIndex) => {
    setIsConditioning(true);
    setCurrentSectionIndex(sectionIndex);
  };
  const handleDeleteConditions = (sectionIndex) => {
    setIsAllConditionsDelete(true);
    setSectionToDeleteId(sections[sectionIndex].id);
    queryClient.invalidateQueries(["conditionsList"]);
  };

  const handleDeleteSectionCondition = () => {
    setIsAllConditionsDelete(false);
    const conditionIds = conditionsList?.results?.map((item) => item.id) ?? [];
    deleteSectionCondition(conditionIds, {
      onSuccess: (res) => {
        notify("All section conditions deleted successfully!", {
          type: "success",
          position: "bottom-left",
        });
        queryClient.invalidateQueries(["conditionsList"]);
      },
      onError: (err) => {
        showError(
          err,
          "A problem occurred while deleting the section conditions."
        );
      },
    });
  };
  return (
    !isSectionsLoading && (
      <Formik
        initialValues={{
          sections: sections,
        }}
        enableReinitialize={true}
        validationSchema={getValidationSchema}
        onSubmit={() => {}}
      >
        {(props) => {
          const currentSectionValid =
            editingSectionIndex !== null &&
            props.values.sections[editingSectionIndex]?.title?.trim().length >
              2 &&
            props.values.sections[editingSectionIndex]?.description?.trim()
              .length >= 2 &&
            props.values.sections[editingSectionIndex]?.questions.length > 0;
          return (
            <Container>
              <H3 fontSize="3">Sections</H3>

              <Flex gap="24">
                <Box width={"20%"}>
                  <GrayCard m={0} padding={0}>
                    <Flex justifyContent={"space-between"}>
                      <StyledUl>
                        <Button
                          isDisabled={editingSectionIndex !== null}
                          variant="text"
                          border="none"
                          backgroundColor="none"
                          color={visibleSection === "all" ? "blue" : "black"}
                          fontWeight={600}
                          padding={0}
                          onClick={() => setVisibleSection("all")}
                        >
                          {" "}
                          All
                        </Button>
                        {sections.map((section, index) => (
                          <Flex justifyContent={"space-between"} key={index} minHeight="auto">
                            <Button
                              textAlign={"start"}
                              isDisabled={editingSectionIndex !== null}
                              variant="text"
                              border="none"
                              padding={0}
                              height='auto'
                              minHeight='auto'
                              fontSize={'13px'}
                              backgroundColor="none"
                              color={
                                visibleSection === section.id ? "blue" : "black"
                              }
                              fontWeight={600}
                              onClick={() => setVisibleSection(section.id)}
                            > <div style={{wordBreak: 'normal', overflowWrap: 'break-word', minWidth: 0}}>
                              {" "}
                              {section.title
                                ? section.title
                                : "Section " + (index + 1)}
                            </div>
                            </Button>
                            {index > 0 && section.id ? (
                              <IconButtonDropdown placement="bottom-end">
                                <>
                                  <IconButtonDropdownOption
                                    IconType={FaPlus}
                                    onClick={() => handleAddConditions(index)}
                                  >
                                    Add Conditions
                                  </IconButtonDropdownOption>
                                  <IconButtonDropdownOption
                                    onClick={() =>
                                      handleDeleteConditions(index)
                                    }
                                    IconType={FaTrash}
                                  >
                                    Delete Conditions
                                  </IconButtonDropdownOption>{" "}
                                </>{" "}
                                : ''
                              </IconButtonDropdown>
                            ) : (
                              ""
                            )}
                          </Flex>
                        ))}
                      </StyledUl>
                    </Flex>
                    <hr></hr>
                    <Flex justifyContent={"space-between"}>
                      <Button
                        variant="grayText"
                        Icon={FaPlus}
                        iconSize="14"
                        p={0}
                        fontWeight={500}
                        isDisabled={
                          (editingSectionIndex !== null)
                        }
                        onClick={() => handleAddSection(props)}
                      >
                        Add Section
                      </Button>
                    </Flex>
                  </GrayCard>
                </Box>
                <Box width={"70%"}>
                  <Form>
                    {sections.filter(section=> visibleSection === "all" || section.id === visibleSection).map((section, index) => {
                      const originalIndex = sections.findIndex(
                        (s) => s.id === section.id
                      );
                      //section.id ==="new"? '': section.id;
                      return (
                        <Box key={index} mb={4}>
                          <H3 color="gray.80" fontSize="2">
                            Section {originalIndex + 1}
                          </H3>
                          {section.isEditing ? (
                            <Box
                              borderBottom={"1px solid"}
                              borderBottomColor="gray.10"
                              pb={2}
                            >
                              <FormRow my={3}>
                                <Input
                                  width={"100%"}
                                  value={
                                    props.values.sections[index]?.title || ""
                                  }
                                  placeholder="Section Title"
                                  label="Section Title"
                                  isRequired
                                  onChange={(e) => {
                                    handleSectionChange(
                                      index,
                                      "title",
                                      e.target.value,
                                      props
                                    );
                                  }}
                                  hasError={Boolean(
                                    props?.errors?.sections?.[index]?.title
                                  )}
                                  errorMessage={
                                    props?.errors?.sections?.[index]?.title
                                  }
                                />
                              </FormRow>
                              <FormRow my={3}>
                                <TextArea
                                  width={"100%"}
                                  value={
                                    props.values.sections[index]?.description ||
                                    ""
                                  }
                                  placeholder="Section Description"
                                  label="Section Description"
                                  rows={4}
                                  onChange={(e) =>
                                    handleSectionChange(
                                      index,
                                      "description",
                                      e.target.value,
                                      props
                                    )
                                  }
                                  hasError={Boolean(
                                    props?.errors?.sections?.[index]
                                      ?.description
                                  )}
                                  errorMessage={
                                    props?.errors?.sections?.[index]
                                      ?.description
                                  }
                                />
                              </FormRow>
                              <Flex gap="24" justifyContent={"space-between"}>
                                <Paragraph>
                                  Questions {section.questions.length}
                                </Paragraph>
                                <Button
                                  Icon={FaPlus}
                                  iconSize="14px"
                                  iconColor="blue.primary"
                                  variant="blueTextWithBorder"
                                  onClick={() => handleAddQuestion(index)}
                                  isDisabled={
                                    section.questions.length >= maxQuestions
                                  }
                                >
                                  Add Question
                                </Button>
                              </Flex>
                              <Box mt={3}>
                                <DragDropContext
                                  onDragEnd={(result) =>
                                    onDragEnd(result, index, props)
                                  }
                                >
                                  <Droppable droppableId={`droppable-${index}`}>
                                    {(provided) => (
                                      <Box
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                      >
                                        {section.questions.map(
                                          (question, qIndex) => (
                                            <Box
                                              key={qIndex}
                                              borderBottom="1px solid"
                                              borderBottomColor="gray.30"
                                              my={2}
                                            >
                                              <Draggable
                                                draggableId={`draggable-${index}-${qIndex}`}
                                                index={qIndex}
                                              >
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                  >
                                                    <Flex
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      mb={2}
                                                    >
                                                      <Box>
                                                        <Flex
                                                          gap="18"
                                                          alignItems={"center"}
                                                        >
                                                          <Box>
                                                            <RiDraggable
                                                              size={18}
                                                              color="#677084"
                                                            />
                                                          </Box>
                                                          <Box>
                                                            <H3
                                                              mb="0"
                                                              fontSize="16px"
                                                            >
                                                              Question{" "}
                                                              {qIndex + 1} -{" "}
                                                              {question.title} -{" "}
                                                              {question
                                                                .field_type
                                                                ?.name ||
                                                                questionsFieldTypeList.data.find(
                                                                  (type) =>
                                                                    type.id ===
                                                                    question.field_type
                                                                )?.name ||
                                                                ""}
                                                            </H3>
                                                            <Paragraph
                                                              my={0}
                                                              color={"gray.60"}
                                                            >
                                                              {
                                                                question.description
                                                              }
                                                            </Paragraph>
                                                          </Box>
                                                        </Flex>
                                                      </Box>
                                                      <Flex gap="12">
                                                        <Image
                                                          src={editIcon}
                                                          alt="edit icon"
                                                          height="24px"
                                                          width="24px"
                                                          onClick={() =>
                                                            handleEditQuestion(
                                                              question.id,
                                                              index,
                                                              qIndex,
                                                              props
                                                            )
                                                          }
                                                          style={{
                                                            cursor: "pointer",
                                                          }}
                                                        />
                                                        <Image
                                                          src={deleteIcon}
                                                          alt="delete icon"
                                                          height="24px"
                                                          width="24px"
                                                          onClick={() =>
                                                            handleDeleteQuestion(
                                                              index,
                                                              qIndex,
                                                              props
                                                            )
                                                          }
                                                          style={{
                                                            cursor: "pointer",
                                                          }}
                                                        />
                                                      </Flex>
                                                    </Flex>
                                                  </div>
                                                )}
                                              </Draggable>
                                            </Box>
                                          )
                                        )}
                                        {provided.placeholder}
                                      </Box>
                                    )}
                                  </Droppable>
                                </DragDropContext>
                              </Box>

                              <Flex gap="12" justifyContent={"end"}>
                                <Button
                                  variant="primary"
                                  onClick={() =>
                                    handleSaveSection(index, props)
                                  }
                                  isDisabled={!currentSectionValid}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  onClick={() => handleToggleEditSection(index)}
                                >
                                  Cancel
                                </Button>
                              </Flex>
                            </Box>
                          ) : (
                            <GrayCard m={"0px"} >
                              <Flex
                                justifyContent={"space-between"}
                                alignItems={"center"}
                              >
                                <H3 fontSize="2">{section.title}</H3>
                                <Flex gap="8">
                                  <Button
                                    Icon={MdModeEdit}
                                    iconPosition="left"
                                    iconSize="16px"
                                    iconColor="gray.60"
                                    variant="grayText"
                                    onClick={() =>
                                      handleToggleEditSection(index)
                                    }
                                    isDisabled={
                                      editingSectionIndex !== null &&
                                      editingSectionIndex !== index
                                    }
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="danger-text"
                                    iconColor="red.primary"
                                    Icon={RiDeleteBin6Line}
                                    onClick={() => {
                                      setSectionToDeleteIndex(index);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </Flex>
                              </Flex>
                              <Paragraph fontSize={1} color={"gray.60"}>
                                {section.description}
                              </Paragraph>
                              {section.questions.map((question, qIndex) => {
                                return (
                                  <Flex
                                    key={qIndex}
                                    gap="12"
                                    alignItems={"center"}
                                  >
                                    <Image
                                      src={
                                        icons[
                                          question.field_type?.slug_name ||
                                            questionsFieldTypeList.data.find(
                                              (type) =>
                                                type.id === question.field_type
                                            )?.slug_name
                                        ]
                                      }
                                      alt={`${question.field_type.name} icon`}
                                      width="22px"
                                      mr={1}
                                    />
                                    <Paragraph
                                      fontSize={1}
                                      color={"gray.60"}
                                      my={1}
                                      key={qIndex}
                                    >
                                      {qIndex + 1} - {question.title} -{" "}
                                      {question.field_type?.name ||
                                        questionsFieldTypeList.data.find(
                                          (type) =>
                                            type.id === question.field_type
                                        )?.name ||
                                        ""}
                                    </Paragraph>
                                  </Flex>
                                );
                              })}
                            </GrayCard>
                          )}
                        </Box>
                      );
                    })}
                    <Flex justifyContent={"end"}>
                      <Button
                        mr={2}
                        isDisabled={editingSectionIndex !== null}
                        onClick={() => navigate(ROUTE.SETTINGS_FORMS)}
                      >
                        Back
                      </Button>
                      <Button
                        isDisabled={editingSectionIndex !== null}
                        onClick={() => handleNavigation()}
                      >
                        Continue
                      </Button>
                    </Flex>
                  </Form>
                </Box>
              </Flex>

              {/* Question Modal */}
              <FormQuestion
                isOpen={isModalOpen}
                onRequestClose={() => setModalOpen(false)}
                onSave={(newQuestion) => handleSaveQuestion(newQuestion, props)}
                question={currentQuestion}
              />
              <Modal
                isOpen={sectionToDeleteIndex !== null}
                onClose={() => setSectionToDeleteIndex(null)}
                title="Delete Section?"
                content="Are you sure you want to delete this section? This action cannot be undone."
                onModalConfirmClick={() =>
                  handleRemoveSection(sectionToDeleteIndex, props)
                }
                secondaryButtonText="Cancel"
                primaryButtonText="Delete"
                primaryButtonVariant="danger"
                hasSecondaryButton
                width="450px"
              />
              <FormConditions
                isOpen={isConditioning}
                onRequestClose={() => setIsConditioning(false)}
                sections={fetchedSections}
                currentIndex={currentSectionIndex}
              />
              <Modal
                isOpen={isAllConditionsDelete}
                onClose={() => setIsAllConditionsDelete(false)}
                title="Delete all conditions?"
                content="All conditions on this section will be permanently deleted."
                onModalConfirmClick={() => handleDeleteSectionCondition()}
                secondaryButtonText="Cancel"
                primaryButtonText="Delete"
                primaryButtonVariant="danger"
                hasSecondaryButton
                width="450px"
              />
            </Container>
          );
        }}
      </Formik>
    )
  );
};
export default SettingsNewFormQuestions;
